// File Sync Service using File System Access API
// Ermöglicht automatische Synchronisation zwischen Web-App und lokalem Ordner

const DB_NAME = 'jaysmart-sync';
const DB_VERSION = 1;

class FileSyncService {
  constructor() {
    this.directoryHandle = null;
    this.syncInterval = null;
    this.isWatching = false;
    this.lastSyncTime = null;
    this.downloadedIds = new Set();
  }

  // Check if File System Access API is supported
  isSupported() {
    return 'showDirectoryPicker' in window;
  }

  // Initialize IndexedDB for storing directory handle
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('config')) {
          db.createObjectStore('config', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('syncedFiles')) {
          db.createObjectStore('syncedFiles', { keyPath: 'id' });
        }
      };
    });
  }

  // Save directory handle to IndexedDB
  async saveDirectoryHandle(handle) {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('config', 'readwrite');
      const store = tx.objectStore('config');
      const request = store.put({ key: 'directoryHandle', value: handle });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Load directory handle from IndexedDB
  async loadDirectoryHandle() {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('config', 'readonly');
      const store = tx.objectStore('config');
      const request = store.get('directoryHandle');
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }

  // Save synced file ID
  async markAsSynced(id) {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('syncedFiles', 'readwrite');
      const store = tx.objectStore('syncedFiles');
      const request = store.put({ id, syncedAt: new Date().toISOString() });
      request.onsuccess = () => {
        this.downloadedIds.add(id);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Check if file was already synced
  async isAlreadySynced(id) {
    if (this.downloadedIds.has(id)) return true;
    
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('syncedFiles', 'readonly');
      const store = tx.objectStore('syncedFiles');
      const request = store.get(id);
      request.onsuccess = () => {
        if (request.result) {
          this.downloadedIds.add(id);
          resolve(true);
        } else {
          resolve(false);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Load all synced IDs into memory
  async loadSyncedIds() {
    const db = await this.initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('syncedFiles', 'readonly');
      const store = tx.objectStore('syncedFiles');
      const request = store.getAll();
      request.onsuccess = () => {
        request.result.forEach(item => this.downloadedIds.add(item.id));
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Let user select a directory
  async selectDirectory() {
    if (!this.isSupported()) {
      throw new Error('File System Access API wird nicht unterstützt. Bitte Chrome oder Edge verwenden.');
    }

    try {
      const handle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      });
      
      this.directoryHandle = handle;
      await this.saveDirectoryHandle(handle);
      
      // Create subfolders
      await this.createSubfolders();
      
      return handle.name;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null; // User cancelled
      }
      throw error;
    }
  }

  // Create category subfolders
  async createSubfolders() {
    if (!this.directoryHandle) return;

    const folders = [
      '_Eingang',
      'Banken',
      'Versicherungen', 
      'Telekommunikation',
      'Behörden',
      'Energie',
      'Miete',
      'Gesundheit',
      'Abonnements',
      'Verträge',
      'Sonstiges'
    ];

    for (const folder of folders) {
      try {
        await this.directoryHandle.getDirectoryHandle(folder, { create: true });
      } catch (error) {
        console.warn(`Konnte Ordner ${folder} nicht erstellen:`, error);
      }
    }
  }

  // Restore previously selected directory
  async restoreDirectory() {
    try {
      const handle = await this.loadDirectoryHandle();
      if (!handle) return null;

      // Verify permission
      const permission = await handle.queryPermission({ mode: 'readwrite' });
      if (permission === 'granted') {
        this.directoryHandle = handle;
        await this.loadSyncedIds();
        return handle.name;
      }

      // Request permission again
      const newPermission = await handle.requestPermission({ mode: 'readwrite' });
      if (newPermission === 'granted') {
        this.directoryHandle = handle;
        await this.loadSyncedIds();
        return handle.name;
      }

      return null;
    } catch (error) {
      console.warn('Konnte Ordner nicht wiederherstellen:', error);
      return null;
    }
  }

  // Get subfolder for document type
  getSubfolder(docType) {
    const mapping = {
      'Banken': 'Banken',
      'Versicherungen': 'Versicherungen',
      'Telekommunikation': 'Telekommunikation',
      'Behörden': 'Behörden',
      'Energie': 'Energie',
      'Miete': 'Miete',
      'Gesundheit': 'Gesundheit',
      'Abonnements': 'Abonnements',
      'Verträge': 'Verträge',
      'Rechnung': 'Sonstiges',
      'Mahnung': 'Sonstiges'
    };
    return mapping[docType] || 'Sonstiges';
  }

  // Download a single document to local folder
  async downloadDocument(doc, token, apiUrl) {
    if (!this.directoryHandle) {
      throw new Error('Kein Sync-Ordner ausgewählt');
    }

    // Check if already synced
    if (await this.isAlreadySynced(doc.id)) {
      return { skipped: true, reason: 'already_synced' };
    }

    try {
      // Fetch file from API
      const response = await fetch(`${apiUrl}/api/sync/files/${doc.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      
      // Get target folder
      const subfolderName = this.getSubfolder(doc.doc_type);
      const subfolder = await this.directoryHandle.getDirectoryHandle(subfolderName, { create: true });
      
      // Create file with unique name
      let fileName = doc.original_name || `${doc.id}.pdf`;
      let fileHandle;
      let counter = 0;
      
      while (true) {
        try {
          const testName = counter === 0 ? fileName : `${fileName.replace(/(\.[^.]+)$/, `_${counter}$1`)}`;
          fileHandle = await subfolder.getFileHandle(testName, { create: false });
          counter++;
        } catch {
          // File doesn't exist, we can use this name
          fileName = counter === 0 ? fileName : `${fileName.replace(/(\.[^.]+)$/, `_${counter}$1`)}`;
          break;
        }
      }

      // Write file
      fileHandle = await subfolder.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();

      // Mark as synced
      await this.markAsSynced(doc.id);

      return { 
        success: true, 
        fileName, 
        folder: subfolderName,
        sender: doc.sender_name 
      };
    } catch (error) {
      console.error(`Fehler beim Download von ${doc.id}:`, error);
      return { success: false, error: error.message };
    }
  }

  // Sync all documents from server
  async syncFromServer(token, apiUrl, onProgress) {
    if (!this.directoryHandle) {
      throw new Error('Kein Sync-Ordner ausgewählt');
    }

    try {
      // Fetch document list
      const response = await fetch(`${apiUrl}/api/sync/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const documents = await response.json();
      
      let downloaded = 0;
      let skipped = 0;
      let failed = 0;

      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: documents.length,
            currentFile: doc.original_name || doc.id
          });
        }

        const result = await this.downloadDocument(doc, token, apiUrl);
        
        if (result.skipped) {
          skipped++;
        } else if (result.success) {
          downloaded++;
        } else {
          failed++;
        }
      }

      this.lastSyncTime = new Date();
      
      return { downloaded, skipped, failed, total: documents.length };
    } catch (error) {
      console.error('Sync-Fehler:', error);
      throw error;
    }
  }

  // Upload file from local folder to server
  async uploadFile(file, token, apiUrl) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${apiUrl}/api/sync/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }

  // Watch _Eingang folder for new files
  async watchInbox(token, apiUrl, onNewFile) {
    if (!this.directoryHandle) return;

    try {
      const inboxHandle = await this.directoryHandle.getDirectoryHandle('_Eingang', { create: true });
      
      // Get list of files already processed
      const processedFiles = new Set();
      
      const checkForNewFiles = async () => {
        try {
          for await (const entry of inboxHandle.values()) {
            if (entry.kind === 'file' && !processedFiles.has(entry.name)) {
              // Check file extension
              const ext = entry.name.split('.').pop().toLowerCase();
              if (['pdf', 'jpg', 'jpeg', 'png', 'webp', 'txt'].includes(ext)) {
                processedFiles.add(entry.name);
                
                // Upload file
                const file = await entry.getFile();
                try {
                  const result = await this.uploadFile(file, token, apiUrl);
                  if (onNewFile) {
                    onNewFile({ 
                      name: entry.name, 
                      success: result.success,
                      documentId: result.document_id,
                      sender: result.sender_name
                    });
                  }
                } catch (error) {
                  if (onNewFile) {
                    onNewFile({ name: entry.name, success: false, error: error.message });
                  }
                }
              }
            }
          }
        } catch (error) {
          console.warn('Fehler beim Überwachen des Eingang-Ordners:', error);
        }
      };

      // Initial check
      await checkForNewFiles();

      // Poll every 5 seconds
      this.syncInterval = setInterval(checkForNewFiles, 5000);
      this.isWatching = true;

    } catch (error) {
      console.error('Konnte Eingang-Ordner nicht überwachen:', error);
    }
  }

  // Stop watching
  stopWatching() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    this.isWatching = false;
  }

  // Get sync status
  getStatus() {
    return {
      hasDirectory: !!this.directoryHandle,
      directoryName: this.directoryHandle?.name || null,
      isWatching: this.isWatching,
      lastSyncTime: this.lastSyncTime,
      syncedCount: this.downloadedIds.size
    };
  }

  // Clear all synced data
  async clearSyncData() {
    const db = await this.initDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['config', 'syncedFiles'], 'readwrite');
      tx.objectStore('config').clear();
      tx.objectStore('syncedFiles').clear();
      tx.oncomplete = () => {
        this.directoryHandle = null;
        this.downloadedIds.clear();
        this.lastSyncTime = null;
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }
}

// Singleton instance
export const fileSyncService = new FileSyncService();
export default fileSyncService;
